import type { StepDetails } from '../../shared/orderStatus';
import { formatTrackingStatus, getTrackingUrl, resolveCarrier } from '../utils/carrierLinks';
import { daysUntil, formatDate } from '../utils/time';

interface TrackingDetailsProps {
  details: StepDetails;
  showCoreCharge?: boolean;
  /** The core is back with us, so the return deadline is shown without urgency. */
  coreReturned?: boolean;
}

export function TrackingDetails({
  details,
  showCoreCharge = false,
  coreReturned = false,
}: TrackingDetailsProps) {
  const { trackingNo, carrier, trackingStatus, expirationDate, coreBalance, assemblyStage, inspectionResult } =
    details;

  const isDelivered = trackingStatus
    ? trackingStatus.toLowerCase().includes('delivered')
    : false;
  const isLabelOnly = trackingStatus?.trim().toLowerCase() === 'pre_transit';
  const carrierName = trackingNo ? resolveCarrier(carrier, trackingNo) : null;

  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs space-y-1.5 border border-slate-100">
      {trackingNo && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-slate-500">Tracking:</span>
          {(() => {
            const url = carrierName ? getTrackingUrl(carrierName, trackingNo) : null;
            return url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:text-brand-700 font-mono underline underline-offset-2"
              >
                {trackingNo}
              </a>
            ) : (
              <span className="font-mono text-slate-700">{trackingNo}</span>
            );
          })()}
          {carrierName && <span className="text-slate-400">via {carrierName}</span>}
          {trackingStatus && (
            <span
              className={`
                px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide
                ${
                  isDelivered
                    ? 'bg-brand-100 text-brand-700'
                    : isLabelOnly
                      ? 'bg-slate-100 text-slate-600'
                      : 'bg-blue-100 text-blue-700'
                }
              `}
            >
              {formatTrackingStatus(trackingStatus)}
            </span>
          )}
        </div>
      )}

      {assemblyStage && (
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Repair stage:</span>
          <span className="font-medium text-slate-700">{assemblyStage}</span>
        </div>
      )}

      {expirationDate && (
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Core return by:</span>
          <span
            className={`font-medium ${
              coreReturned
                ? 'text-slate-700'
                : daysUntil(expirationDate) <= 7
                  ? 'text-step-rejected'
                  : daysUntil(expirationDate) <= 14
                    ? 'text-step-warning'
                    : 'text-slate-700'
            }`}
          >
            {formatDate(expirationDate)}
          </span>
          {coreReturned ? null : daysUntil(expirationDate) > 0 ? (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                daysUntil(expirationDate) <= 7
                  ? 'bg-red-100 text-red-700'
                  : daysUntil(expirationDate) <= 14
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-500'
              }`}
            >
              {daysUntil(expirationDate)}d left
            </span>
          ) : (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700">
              EXPIRED
            </span>
          )}
        </div>
      )}

      {showCoreCharge && coreBalance !== undefined && (
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Core charge:</span>
          <span className="font-semibold text-slate-700">${coreBalance.toFixed(2)}</span>
        </div>
      )}

      {inspectionResult && (
        <div className="flex items-center gap-2">
          <span className="text-slate-500">Inspection:</span>
          <span className="font-medium text-slate-700">{inspectionResult}</span>
        </div>
      )}
    </div>
  );
}
